import { useState } from 'react';
import { ExportOptions } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

interface ExportPanelProps {
  onExport: (options: ExportOptions) => void;
  totalRecords: number;
  filteredRecords: number;
}

const ExportPanel = ({ onExport, totalRecords, filteredRecords }: ExportPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [format, setFormat] = useState<'excel' | 'csv'>('excel');
  const [includeColumns, setIncludeColumns] = useState<string[]>([
    'productCode',
    'productName',
    'ncm',
    'icms',
    'ipi',
    'pis',
    'cofins',
    'totalTax',
    'effectiveRate',
    'status'
  ]);
  const [filterApplied, setFilterApplied] = useState(false);

  const availableColumns = [
    { value: 'productCode', label: 'Código do Produto' },
    { value: 'productName', label: 'Nome do Produto' },
    { value: 'ncm', label: 'NCM' },
    { value: 'cest', label: 'CEST' },
    { value: 'icms', label: 'ICMS' },
    { value: 'ipi', label: 'IPI' },
    { value: 'pis', label: 'PIS' },
    { value: 'cofins', label: 'COFINS' },
    { value: 'totalTax', label: 'Total de Impostos' },
    { value: 'effectiveRate', label: 'Alíquota Efetiva' },
    { value: 'cst', label: 'CST' },
    { value: 'cfop', label: 'CFOP' },
    { value: 'ufOrigin', label: 'UF Origem' },
    { value: 'ufDestination', label: 'UF Destino' },
    { value: 'status', label: 'Status' },
    { value: 'calculatedAt', label: 'Data do Cálculo' }
  ];

  const handleColumnToggle = (column: string, checked: boolean) => {
    if (checked) {
      setIncludeColumns([...includeColumns, column]);
    } else {
      setIncludeColumns(includeColumns.filter(c => c !== column));
    }
  };

  const handleExport = () => {
    onExport({
      format,
      includeColumns,
      filterApplied
    });
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Button
        variant="default"
        onClick={() => setIsOpen(!isOpen)}
        iconName="Download"
        iconPosition="left"
      >
        Exportar Resultados
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-96 bg-card rounded-lg border border-border shadow-lg z-50 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Opções de Exportação</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-muted rounded transition-colors"
                aria-label="Fechar"
              >
                <Icon name="X" size={20} color="var(--color-muted-foreground)" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Formato do Arquivo
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFormat('excel')}
                    className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                      format === 'excel' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Icon name="FileSpreadsheet" size={16} color="currentColor" />
                      <span className="text-sm font-medium">Excel</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setFormat('csv')}
                    className={`flex-1 px-4 py-2 rounded-md border transition-colors ${
                      format === 'csv' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Icon name="FileText" size={16} color="currentColor" />
                      <span className="text-sm font-medium">CSV</span>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <Checkbox
                  label="Aplicar filtros ativos"
                  description={`Exportar apenas ${filteredRecords} de ${totalRecords} registros`}
                  checked={filterApplied}
                  onChange={(e) => setFilterApplied(e.target.checked)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Colunas para Exportar
                </label>
                <div className="max-h-64 overflow-y-auto space-y-2 p-2 bg-muted rounded-md">
                  {availableColumns.map((column) => (
                    <Checkbox
                      key={column.value}
                      label={column.label}
                      checked={includeColumns.includes(column.value)}
                      onChange={(e) => handleColumnToggle(column.value, e.target.checked)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setIsOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleExport}
                  disabled={includeColumns.length === 0}
                  iconName="Download"
                  iconPosition="left"
                >
                  Exportar
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportPanel;